import React from 'react';
import { SerializedStyles, css } from '@emotion/react';
import { useTheme } from '@veneer/theme';
import getPillBorder from '../../utils/get_pill_border';
import { makeVerticalVisualCue } from '../../utils/visual_cue';
import { focusRingStyle } from '../../utils/focus_ring';

type StylesType = {
  root: {
    base: SerializedStyles;
    withIcon: SerializedStyles;
    pill: {
      base: SerializedStyles;
      withIcon: SerializedStyles;
    };
    hc: SerializedStyles;
    multiple: SerializedStyles;
  };
  label: SerializedStyles;
  text: {
    base: SerializedStyles;
    wrap: SerializedStyles;
  };
  icons: SerializedStyles;
  customIcon: SerializedStyles;
  multiSelectIcon: SerializedStyles;
};

const useStyles = (): StylesType => {
  const theme = useTheme();
  const visualCue = makeVerticalVisualCue(theme, theme.layout.size3, 2);
  const visualCuePill = makeVerticalVisualCue(theme, theme.layout.size2, 2);

  return React.useMemo(
    () => ({
      root: {
        base: css({
          alignItems: 'center',
          borderRadius: theme.borderRadius3,
          boxSizing: 'border-box',
          color: theme.color.foreground.default,
          cursor: 'pointer',
          display: 'flex',
          pointerEvents: 'auto',
          position: 'relative',
          textDecoration: 'none',
          transition: 'background-color 100ms linear, color 100ms linear',
          userSelect: 'none',
          padding: `6px ${theme.layout.size3}`,
          gap: theme.layout.size2,

          '&::before': visualCue,

          '&:hover': {
            backgroundColor: theme.color.neutral.background.hover,
            color: theme.color.foreground.default,

            '&::before': {
              opacity: 1,
              backgroundColor: theme.color.neutral.base.hover,
            },
          },

          '&:active': {
            backgroundColor: theme.color.neutral.background.active,
            color: theme.color.foreground.default,

            '&::before': {
              opacity: 1,
              backgroundColor: theme.color.neutral.base.active,
            },
          },

          '&[data-focus="true"]': {
            ...focusRingStyle({
              theme,
              outlineOffset: -2,
              shadowSize: 4,
              shadowInset: true,
            }),
          },

          '&[aria-selected="true"]': {
            backgroundColor: theme.color.primary.background.active,
            color: theme.color.primary.base.active,

            '&::before': {
              opacity: 1,
              backgroundColor: theme.color.primary.base.active,
            },

            '&:hover': {
              backgroundColor: theme.color.primary.background.active,
              color: theme.color.primary.base.active,
            },

            '&:active': {
              backgroundColor: theme.color.primary.background.active,
              color: theme.color.primary.base.active,
            },
          },

          '&[aria-disabled="true"]': {
            pointerEvents: 'none',
            backgroundColor: 'transparent',
            color: theme.color.foreground.disabled,
            '&::before': { opacity: 0 },
          },
        }),

        withIcon: css({
          paddingLeft: theme.layout.size2,
        }),

        pill: {
          base: css({
            borderRadius: getPillBorder,
            '&::before': visualCuePill,
            padding: `6px ${theme.layout.size4}`,
          }),
          withIcon: css({
            paddingLeft: theme.layout.size3,
          }),
        },

        hc: css({
          color: theme.color.primary.base.default,
          '&:hover, &:active': {
            color: theme.color.primary.foreground.default,
          },
          '&[aria-selected="true"]': {
            backgroundColor: theme.color.primary.base.default,
            color: theme.color.primary.foreground.default,
            '&:hover, &:active': {
              color: theme.color.primary.foreground.default,
            },
          },
          '&[aria-disabled="true"]': {
            backgroundColor: 'transparent',
            color: theme.color.foreground.disabled,
          },
        }),

        multiple: css({
          '&[aria-selected="true"] ': {
            '.icon_x': { display: 'none' },
            '.icon_checkmark': { display: 'block' },

            '&:hover': {
              backgroundColor: theme.color.neutral.background.hover,
              color: theme.color.foreground.default,
              '.icon_x': { display: 'block' },
              '.icon_checkmark': { display: 'none' },
              '&::before': { backgroundColor: theme.color.neutral.base.hover },
            },

            '&:active': {
              backgroundColor: theme.color.neutral.background.active,
              color: theme.color.foreground.default,
              '&::before': { backgroundColor: theme.color.neutral.base.active },
            },
          },
        }),
      },

      icons: css({
        gap: '2px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }),
      customIcon: css({
        color: 'inherit',
      }),
      multiSelectIcon: css({
        display: 'none',
        color: 'inherit',
        height: theme.layout.size4,
        width: theme.layout.size4,
        minWidth: theme.layout.size4,
      }),

      label: css({
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: theme.layout.size6,
        overflow: 'hidden',
        ...theme.typography.labelRegular,
      }),

      text: {
        base: css({
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }),
        wrap: css({
          textWrap: 'wrap',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }),
      },
    }),
    [theme, visualCue, visualCuePill]
  );
};

export default useStyles;
